import theme from "../../../../styles/theme";

/* ============================================================================
   FACTORY — IDS STABLES
============================================================================ */
const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const createWorkbenchItemId = (prefix, family, title) => {
  const base = [prefix, family, slugify(title)].filter(Boolean).join("-");
  return `${base}`;
};

/* ============================================================================
   MÉTADONNÉES FAMILLES
============================================================================ */
export const familyMeta = {
  pressure: {
    label: "Hydraulic",
    shortLabel: "Hydraulic",
    color: theme.SystemDisciplines.getColor("HYD") || theme.colors.text.green,
    lightBg: theme.Generic.teal.pastel.bg,
    lightBorder: theme.Generic.teal.pastel.border,
  },
  gravity: {
    label: "Gravity",
    shortLabel: "Gravity",
    color: theme.colors.text.stream,
    lightBg: theme.Generic.grey.pastel.bg,
    lightBorder: theme.Generic.grey.pastel.border,
  },
  pumping: {
    label: "Pumping",
    shortLabel: "Pumping",
    color: theme.colors.text.secondary,
    lightBg: theme.Generic.orange.pastel.bg,
    lightBorder: theme.Generic.orange.pastel.border,
  },
  storage: {
    label: "Storage",
    shortLabel: "Storage",
    color: theme.colors.text.primary,
    lightBg: theme.Generic.blue.pastel.bg,
    lightBorder: theme.Generic.blue.pastel.border,
  },
  hvac: {
    label: "HVAC",
    shortLabel: "HVAC",
    color: theme.SystemDisciplines.getColor("MEC") || theme.colors.text.third,
    lightBg: theme.Generic.grey.pastel.bg,
    lightBorder: theme.Generic.grey.pastel.border,
  },
  electrical: {
    label: "Electrical",
    shortLabel: "Electrical",
    color: theme.SystemDisciplines.getColor("ELE") || theme.colors.text.orbit,
    lightBg: theme.Generic.blue.pastel.bg,
    lightBorder: theme.Generic.blue.pastel.border,
  },
  automation: {
    label: "Automation",
    shortLabel: "Automation",
    color: theme.Generic.purple.dark.bg,
    lightBg: theme.Generic.purple.pastel.bg,
    lightBorder: theme.Generic.purple.pastel.border,
  },
  process: {
    label: "Process",
    shortLabel: "Process",
    color: theme.colors.text.caneva,
    lightBg: theme.Generic.yellow.pastel.bg,
    lightBorder: theme.Generic.yellow.pastel.border,
  },
};

/* ============================================================================
   SCHÉMA DES CHAMPS
   ----------------------------------------------------------------------------
   Structure stable et sérialisable.
============================================================================ */
export const fieldSchema = {
  pressure: {
    summarySourceField: "length",
    quickFields: [
      { name: "length", label: "Long.", control: "number", labelWidth: 58 },
      { name: "diameter", label: "Ø", control: "number", labelWidth: 58 },
      {
        name: "material",
        label: "Mat.",
        control: "select",
        labelWidth: 58,
        options: [
          { value: "DI", label: "Fonte ductile" },
          { value: "PEHD", label: "PEHD" },
          { value: "PVC", label: "PVC" },
          { value: "INOX", label: "Inox" },
        ],
      },
    ],
    detailFields: baseDetailFields(),
  },

  gravity: {
    summarySourceField: "length",
    quickFields: [
      { name: "length", label: "Long.", control: "number", labelWidth: 58 },
      { name: "diameter", label: "Ø", control: "number", labelWidth: 58 },
      {
        name: "material",
        label: "Mat.",
        control: "select",
        labelWidth: 58,
        options: [
          { value: "PVC", label: "PVC" },
          { value: "PRV", label: "PRV" },
          { value: "BETON", label: "Béton" },
          { value: "GRES", label: "Grès" },
        ],
      },
    ],
    detailFields: baseDetailFields(),
  },

  pumping: {
    summarySourceField: "flow",
    quickFields: [
      { name: "flow", label: "Débit", control: "number", labelWidth: 58 },
      { name: "count", label: "Pompes", control: "stepper" },
      { name: "power", label: "kW", control: "number", labelWidth: 58 },
    ],
    detailFields: baseDetailFields(),
  },

  storage: {
    summarySourceField: "capacity",
    quickFields: [
      { name: "capacity", label: "Cap.", control: "number", labelWidth: 58 },
      { name: "count", label: "Nb", control: "stepper" },
      {
        name: "type",
        label: "Type",
        control: "select",
        labelWidth: 58,
        options: [
          { value: "Béton", label: "Béton" },
          { value: "Acier", label: "Acier" },
          { value: "PEHD", label: "PEHD" },
        ],
      },
    ],
    detailFields: baseDetailFields(),
  },

  hvac: {
    summarySourceField: "zones",
    quickFields: [
      { name: "zones", label: "Zones", control: "stepper" },
      { name: "power", label: "kW", control: "number", labelWidth: 58 },
      {
        name: "type",
        label: "Type",
        control: "select",
        labelWidth: 58,
        options: [
          { value: "VRV", label: "VRV" },
          { value: "CTA", label: "CTA" },
          { value: "Split", label: "Split" },
          { value: "Mixte", label: "Mixte" },
        ],
      },
    ],
    detailFields: baseDetailFields(),
  },

  electrical: {
    summarySourceField: "power",
    quickFields: [
      { name: "power", label: "kVA", control: "number", labelWidth: 58 },
      { name: "boards", label: "TGBT", control: "stepper" },
      {
        name: "backup",
        label: "Sec.",
        control: "select",
        labelWidth: 58,
        options: [
          { value: "UPS", label: "UPS" },
          { value: "GE", label: "Groupe" },
          { value: "Groupe + UPS", label: "Groupe + UPS" },
          { value: "Aucun", label: "Aucun" },
        ],
      },
    ],
    detailFields: baseDetailFields(),
  },

  automation: {
    summarySourceField: "points",
    quickFields: [
      { name: "points", label: "Pts", control: "number", labelWidth: 58 },
      { name: "panels", label: "Arm.", control: "stepper" },
      {
        name: "architecture",
        label: "Arch.",
        control: "select",
        labelWidth: 58,
        options: [
          { value: "PLC", label: "PLC" },
          { value: "PLC + SCADA", label: "PLC + SCADA" },
          { value: "DCS", label: "DCS" },
        ],
      },
    ],
    detailFields: baseDetailFields(),
  },

  process: {
    summarySourceField: "lines",
    quickFields: [
      { name: "lines", label: "Lignes", control: "stepper" },
      { name: "capacity", label: "Cap.", control: "number", labelWidth: 58 },
      {
        name: "processType",
        label: "Proc.",
        control: "select",
        labelWidth: 58,
        options: [
          { value: "Filtration", label: "Filtration" },
          { value: "Décantation", label: "Décantation" },
          { value: "Boues", label: "Boues" },
          { value: "Désinfection", label: "Désinfection" },
        ],
      },
    ],
    detailFields: baseDetailFields(),
  },
};

function baseDetailFields() {
  return [
    {
      name: "redundancy",
      label: "Redond.",
      control: "select",
      labelWidth: 64,
      options: [
        { value: "Simple", label: "Simple" },
        { value: "Standard", label: "Standard" },
        { value: "Partielle", label: "Partielle" },
        { value: "N", label: "N" },
        { value: "N+1", label: "N+1" },
        { value: "2N", label: "2N" },
      ],
    },
    {
      name: "notes",
      label: "Note",
      control: "text",
      labelWidth: 64,
      placeholder: "Observation rapide...",
    },
  ];
}

/* ============================================================================
   DONNÉES INITIALES
============================================================================ */
export const createDefaultTechnicalSystems = () => [
  {
    id: "pressure-network",
    entityType: "TECHNICAL_SYSTEM",
    family: "pressure",
    title: "Réseau sous pression",
    subtitle: "Distribution / refoulement",
    active: true,
    unit: "ml",
    summaryValue: 1450,
    summaryLabel: "Longueur estimée",
    fields: {
      length: 1450,
      diameter: 250,
      material: "DI",
      redundancy: "Standard",
      notes: "",
    },
  },
  {
    id: "gravity-network",
    entityType: "TECHNICAL_SYSTEM",
    family: "gravity",
    title: "Réseau gravitaire",
    subtitle: "Collecte / évacuation",
    active: true,
    unit: "ml",
    summaryValue: 980,
    summaryLabel: "Longueur estimée",
    fields: {
      length: 980,
      diameter: 400,
      material: "PVC",
      redundancy: "Simple",
      notes: "",
    },
  },
  {
    id: "pumping",
    entityType: "TECHNICAL_SYSTEM",
    family: "pumping",
    title: "Pompage",
    subtitle: "Stations / groupes",
    active: true,
    unit: "m³/h",
    summaryValue: 120,
    summaryLabel: "Débit de référence",
    fields: {
      flow: 120,
      count: 3,
      power: 45,
      redundancy: "N+1",
      notes: "",
    },
  },
  {
    id: "storage",
    entityType: "TECHNICAL_SYSTEM",
    family: "storage",
    title: "Stockage",
    subtitle: "Réservoirs / cuves",
    active: false,
    unit: "m³",
    summaryValue: 500,
    summaryLabel: "Capacité indicative",
    fields: {
      capacity: 500,
      count: 1,
      type: "Béton",
      redundancy: "Simple",
      notes: "",
    },
  },
  {
    id: "hvac",
    entityType: "TECHNICAL_SYSTEM",
    family: "hvac",
    title: "HVAC",
    subtitle: "Ventilation / climatisation",
    active: false,
    unit: "zones",
    summaryValue: 6,
    summaryLabel: "Zones estimées",
    fields: {
      zones: 6,
      power: 85,
      type: "VRV",
      redundancy: "Standard",
      notes: "",
    },
  },
  {
    id: "electrical",
    entityType: "TECHNICAL_SYSTEM",
    family: "electrical",
    title: "Électricité",
    subtitle: "Distribution / secours",
    active: true,
    unit: "kVA",
    summaryValue: 630,
    summaryLabel: "Puissance estimée",
    fields: {
      power: 630,
      boards: 4,
      backup: "Groupe + UPS",
      redundancy: "N",
      notes: "",
    },
  },
  {
    id: "automation",
    entityType: "TECHNICAL_SYSTEM",
    family: "automation",
    title: "Automatisation / contrôle",
    subtitle: "SCADA / instrumentation",
    active: true,
    unit: "points",
    summaryValue: 180,
    summaryLabel: "Points estimés",
    fields: {
      points: 180,
      panels: 3,
      architecture: "PLC + SCADA",
      redundancy: "Partielle",
      notes: "",
    },
  },
  {
    id: "process",
    entityType: "TECHNICAL_SYSTEM",
    family: "process",
    title: "Traitement / process",
    subtitle: "Équipements principaux",
    active: false,
    unit: "lignes",
    summaryValue: 2,
    summaryLabel: "Lignes pressenties",
    fields: {
      lines: 2,
      capacity: 150,
      processType: "Filtration",
      redundancy: "Simple",
      notes: "",
    },
  },
];